import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Plus, Image, Users, Eye, Clock, AlertTriangle, Activity, Upload, ArrowRight } from 'lucide-react'
import { useMemo, useState, useEffect } from 'react'
import { getDrops, getTotalViews, getTotalPhotos, getTotalContributors, getDropsNearRetentionExpiry, FREE_TIER_RETENTION_DAYS, getRecentActivity, type Drop, type RecentActivity } from '@/lib/dropsStore'
import { useAuth } from '@/contexts/AuthContext'
import { checkAndNotifyClosingDrops } from '@/lib/notificationsStore'
import { getAllInvitations } from '@/lib/contributorInvitationsStore'
import { DEMO_AUTH_ENABLED } from '@/lib/env'
import { useDropsQuery } from '@/data/drops'

export default function Dashboard() {
  return DEMO_AUTH_ENABLED ? <DemoDashboard /> : <RealDashboard />
}

function DemoDashboard() {
  const [drops, setDrops] = useState<Drop[]>([])
  const [loading, setLoading] = useState(true)
  const [expiringDrops, setExpiringDrops] = useState<Drop[]>([])
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([])
  const { organization } = useAuth()
  const isFreeTier = organization?.tier === 'free'

  // Load drops from shared store
  useEffect(() => {
    const loadDrops = () => {
      const storedDrops = getDrops()
      setDrops(storedDrops)
      setLoading(false)

      // Check for drops near retention expiry (free tier only)
      if (isFreeTier) {
        setExpiringDrops(getDropsNearRetentionExpiry())
      }

      // Load recent activity
      setRecentActivity(getRecentActivity(5))

      // Check for drops closing soon and notify contributors
      const invitations = getAllInvitations()
      checkAndNotifyClosingDrops(storedDrops, invitations)
    }

    loadDrops()

    // Refresh drops every 5 seconds to get updated stats
    const interval = setInterval(loadDrops, 5000)
    return () => clearInterval(interval)
  }, [isFreeTier])

  const hasDrops = drops.length > 0

  // Calculate stats from drops using the shared store's helper functions
  const stats = useMemo(() => [
    { name: 'Total Drops', value: drops.length.toString(), icon: Image, color: 'from-primary to-primary/80' },
    { name: 'Total Photos', value: getTotalPhotos().toString(), icon: Image, color: 'from-secondary to-secondary/80' },
    { name: 'Total Contributors', value: getTotalContributors().toString(), icon: Users, color: 'from-accent to-accent/80' },
    { name: 'Total Views', value: getTotalViews().toString(), icon: Eye, color: 'from-primary to-secondary' },
  ], [drops])

  const recentDrops = drops.slice(0, 4)

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full bg-gradient-to-r from-primary to-secondary animate-spin mx-auto flex items-center justify-center">
            <div className="w-10 h-10 rounded-full bg-background" />
          </div>
          <p className="mt-4 text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  // Format retention expiry date
  const formatRetentionDate = (date: Date) => {
    const d = new Date(date)
    const now = new Date()
    const diffMs = d.getTime() - now.getTime()
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays <= 0) return 'today'
    if (diffDays === 1) return 'tomorrow'
    return `in ${diffDays} days`
  }

  return (
    <div className="space-y-8">
      {/* Retention Warning Banner - Free Tier Only */}
      {isFreeTier && expiringDrops.length > 0 && (
        <div className="bg-accent/10 border border-accent/20 rounded-2xl p-5 animate-fade-in-up">
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-xl bg-accent/15">
              <AlertTriangle className="h-5 w-5 text-accent" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-foreground">
                {expiringDrops.length === 1 ? 'Drop' : 'Drops'} expiring soon
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                On the Free plan, closed drops are retained for {FREE_TIER_RETENTION_DAYS} days.
              </p>
              <ul className="mt-3 space-y-1">
                {expiringDrops.map(drop => (
                  <li key={drop.id} className="text-sm text-foreground">
                    <Link to={`/drops/${drop.id}`} className="font-medium hover:underline">
                      {drop.name}
                    </Link>
                    {drop.retentionExpiresAt && (
                      <span className="text-muted-foreground"> — expires {formatRetentionDate(drop.retentionExpiresAt)}</span>
                    )}
                  </li>
                ))}
              </ul>
              <Link
                to="/settings/billing"
                className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:text-primary/90 mt-3 group"
              >
                Upgrade to Pro or Business
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Welcome back! Here's an overview of your drops.</p>
        </div>
        <Button asChild className="group">
          <Link to="/drops/create">
            <Plus className="mr-2 h-4 w-4" />
            Create Drop
          </Link>
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <div
            key={stat.name}
            className="bg-card p-6 rounded-2xl border hover:border-primary/30 transition-all duration-300 hover:shadow-lg group stagger-item"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.color} group-hover:scale-110 transition-transform`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{stat.name}</p>
                <p className="text-3xl font-bold tracking-tight">{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {hasDrops ? (
        <>
          {/* Recent Drops */}
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Recent Drops</h2>
              <Button variant="ghost" size="sm" asChild className="group">
                <Link to="/drops">
                  View All
                  <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {recentDrops.map((drop, index) => (
                <DropCard key={drop.id} drop={drop} index={index} />
              ))}
            </div>
          </div>

          {/* Activity Feed */}
          <ActivityFeed activities={recentActivity} />
        </>
      ) : (
        /* Empty State */
        <div className="bg-card rounded-2xl border p-12 text-center card-playful">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mx-auto">
            <Image className="h-8 w-8 text-primary" />
          </div>
          <h3 className="mt-6 text-xl font-bold">No drops yet</h3>
          <p className="mt-2 text-muted-foreground max-w-sm mx-auto">
            Get started by creating your first photo drop and share it with friends
          </p>
          <Button className="mt-8 group" size="lg" asChild>
            <Link to="/drops/create">
              <Plus className="mr-2 h-4 w-4" />
              Create Your First Drop
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>
      )}
    </div>
  )
}

function RealDashboard() {
  const { organization } = useAuth()
  const isFreeTier = organization?.tier === 'free'
  const { drops, isLoading: loading, error, refetch } = useDropsQuery()

  const stats = useMemo(() => {
    const totalDrops = drops.length
    const totalPhotos = drops.reduce((acc, d) => acc + d.photoCount, 0)
    const totalContributors = drops.reduce((acc, d) => acc + d.contributorCount, 0)
    const totalViews = drops.reduce((acc, d) => acc + d.viewCount, 0)
    return [
      { name: 'Total Drops', value: totalDrops.toString(), icon: Image, color: 'from-primary to-primary/80' },
      { name: 'Total Photos', value: totalPhotos.toString(), icon: Image, color: 'from-secondary to-secondary/80' },
      { name: 'Total Contributors', value: totalContributors.toString(), icon: Users, color: 'from-accent to-accent/80' },
      { name: 'Total Views', value: totalViews.toString(), icon: Eye, color: 'from-primary to-secondary' },
    ]
  }, [drops])

  const recentDrops = drops.slice(0, 4)

  const expiringDrops = useMemo(() => {
    if (!isFreeTier) return []
    const now = Date.now()
    const soonMs = 3 * 24 * 60 * 60 * 1000
    return drops.filter((d) => d.retentionExpiresAt && d.retentionExpiresAt.getTime() - now <= soonMs)
  }, [drops, isFreeTier])

  const formatRetentionDate = (date: Date) => {
    const d = new Date(date)
    const now = new Date()
    const diffMs = d.getTime() - now.getTime()
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays <= 0) return 'today'
    if (diffDays === 1) return 'tomorrow'
    return `in ${diffDays} days`
  }

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full bg-gradient-to-r from-primary to-secondary animate-spin mx-auto flex items-center justify-center">
            <div className="w-10 h-10 rounded-full bg-background" />
          </div>
          <p className="mt-4 text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-center max-w-md">
          <h2 className="text-lg font-semibold">Failed to load dashboard</h2>
          <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
          <div className="mt-4">
            <Button onClick={() => refetch()}>Retry</Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {isFreeTier && expiringDrops.length > 0 && (
        <div className="bg-accent/10 border border-accent/20 rounded-2xl p-5 animate-fade-in-up">
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-xl bg-accent/15">
              <AlertTriangle className="h-5 w-5 text-accent" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-foreground">
                {expiringDrops.length === 1 ? 'Drop' : 'Drops'} expiring soon
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                On the Free plan, closed drops are retained for {FREE_TIER_RETENTION_DAYS} days.
              </p>
              <ul className="mt-3 space-y-1">
                {expiringDrops.map(drop => (
                  <li key={drop.id} className="text-sm text-foreground">
                    <Link to={`/drops/${drop.id}`} className="font-medium hover:underline">
                      {drop.name}
                    </Link>
                    {drop.retentionExpiresAt && (
                      <span className="text-muted-foreground"> — expires {formatRetentionDate(drop.retentionExpiresAt)}</span>
                    )}
                  </li>
                ))}
              </ul>
              <Link
                to="/settings/billing"
                className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:text-primary/90 mt-3 group"
              >
                Upgrade to Pro or Business
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Welcome back! Here's an overview of your drops.</p>
        </div>
        <Button asChild className="group">
          <Link to="/drops/create">
            <Plus className="mr-2 h-4 w-4" />
            Create Drop
          </Link>
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <div
            key={stat.name}
            className="bg-card p-6 rounded-2xl border hover:border-primary/30 transition-all duration-300 hover:shadow-lg group stagger-item"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.color} group-hover:scale-110 transition-transform`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{stat.name}</p>
                <p className="text-3xl font-bold tracking-tight">{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {drops.length > 0 ? (
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold">Recent Drops</h2>
            <Button variant="ghost" size="sm" asChild className="group">
              <Link to="/drops">
                View All
                <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {recentDrops.map((drop, index) => (
              <Link
                key={drop.id}
                to={`/drops/${drop.id}`}
                className="bg-card rounded-2xl border overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group stagger-item"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="aspect-video bg-gradient-to-br from-muted to-muted/50 flex items-center justify-center relative overflow-hidden">
                  {drop.coverPhotoUrl ? (
                    <img
                      src={drop.coverPhotoUrl}
                      alt={drop.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <Image className="h-10 w-10 text-muted-foreground/50" />
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-bold truncate group-hover:text-primary transition-colors">{drop.name}</h3>
                  <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1.5">
                      <Image className="h-4 w-4" />
                      <span className="font-medium">{drop.photoCount}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Users className="h-4 w-4" />
                      <span className="font-medium">{drop.contributorCount}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-primary">
                      <Clock className="h-4 w-4" />
                      <span className="font-medium">{drop.status}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-card rounded-2xl border p-12 text-center card-playful">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mx-auto">
            <Image className="h-8 w-8 text-primary" />
          </div>
          <h3 className="mt-6 text-xl font-bold">No drops yet</h3>
          <p className="mt-2 text-muted-foreground max-w-sm mx-auto">
            Get started by creating your first photo drop and share it with friends
          </p>
          <Button className="mt-8 group" size="lg" asChild>
            <Link to="/drops/create">
              <Plus className="mr-2 h-4 w-4" />
              Create Your First Drop
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>
      )}
    </div>
  )
}

function DropCard({ drop, index }: { drop: Drop; index: number }) {
  const formatTimeRemaining = (date: Date) => {
    const diff = date.getTime() - Date.now()
    if (diff <= 0) return 'Expired'
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    if (days > 0) return `${days}d left`
    const hours = Math.floor(diff / (1000 * 60 * 60))
    return `${hours}h left`
  }

  return (
    <Link
      to={`/drops/${drop.id}`}
      className="bg-card rounded-2xl border overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group stagger-item"
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      {/* Cover Image or Placeholder */}
      <div className="aspect-video bg-gradient-to-br from-muted to-muted/50 flex items-center justify-center relative overflow-hidden">
        {drop.coverUrl ? (
          <img
            src={drop.coverUrl}
            alt={drop.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <Image className="h-10 w-10 text-muted-foreground/50" />
        )}
        {/* Status Badge */}
        <div className="absolute top-3 right-3">
          <span
            className={`px-3 py-1 rounded-full text-xs font-semibold shadow-sm ${
              drop.status === 'active'
                ? 'bg-gradient-to-r from-green-400 to-emerald-500 text-white'
                : 'bg-muted text-muted-foreground'
            }`}
          >
            {drop.status === 'active' ? 'Active' : 'Closed'}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-bold truncate group-hover:text-primary transition-colors">{drop.name}</h3>

        {/* Stats */}
        <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <Image className="h-4 w-4" />
            <span className="font-medium">{drop.photoCount}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Users className="h-4 w-4" />
            <span className="font-medium">{drop.contributorCount}</span>
          </div>
          {drop.status === 'active' && drop.expiresAt && (
            <div className="flex items-center gap-1.5 text-primary">
              <Clock className="h-4 w-4" />
              <span className="font-medium">{formatTimeRemaining(drop.expiresAt)}</span>
            </div>
          )}
        </div>
      </div>
    </Link>
  )
}

// Activity Feed component
function ActivityFeed({ activities }: { activities: RecentActivity[] }) {
  const formatActivityTime = (timestamp: number) => {
    const diff = Date.now() - timestamp
    const minutes = Math.floor(diff / (1000 * 60))
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))

    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    if (hours < 24) return `${hours}h ago`
    if (days === 1) return 'Yesterday'
    return `${days}d ago`
  }

  if (activities.length === 0) {
    return (
      <div>
        <div className="flex items-center gap-2 mb-6">
          <div className="p-2 rounded-lg bg-primary/10">
            <Activity className="h-5 w-5 text-primary" />
          </div>
          <h2 className="text-xl font-bold">Recent Activity</h2>
        </div>
        <div className="bg-card rounded-2xl border p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto">
            <Activity className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="mt-4 font-medium">No recent activity</p>
          <p className="text-sm text-muted-foreground mt-1">Activity will appear here when contributors upload photos</p>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-6">
        <div className="p-2 rounded-lg bg-primary/10">
          <Activity className="h-5 w-5 text-primary" />
        </div>
        <h2 className="text-xl font-bold">Recent Activity</h2>
      </div>
      <div className="bg-card rounded-2xl border overflow-hidden divide-y">
        {activities.map((activity, index) => (
          <Link
            key={`${activity.dropId}-${activity.timestamp}-${index}`}
            to={`/drops/${activity.dropId}`}
            className="flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors group"
          >
            <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
              <Upload className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm">
                <span className="font-semibold">{activity.photoCount} photo{activity.photoCount !== 1 ? 's' : ''}</span>
                {' uploaded to '}
                <span className="font-semibold text-primary">{activity.dropName}</span>
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {formatActivityTime(activity.timestamp)}
              </p>
            </div>
            <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
          </Link>
        ))}
      </div>
    </div>
  )
}
